import Vue from 'vue';

/**
 * Confirmation dialog service for Element UI.
 * Provides reactive methods for opening different types of confirmation dialogs (success, warning, error, info).
 */

/**
 * Options per dialog di conferma Element UI
 */
export interface ConfirmOptions {
	confirmButtonText?: string;
	cancelButtonText?: string;
	type?: 'success' | 'warning' | 'error' | 'info';
	confirmButtonClass?: string;
}

/**
 * Callback function type
 */
type ConfirmCallback = (param1?: any, param2?: any) => void;

/**
 * Internal function to open a confirmation dialog using Element UI.
 * @description Handles dialog creation, callback execution, and error handling.
 * @param options - Dialog styling and button options
 * @param callback - Function to execute on confirmation
 * @param param1 - First parameter passed to callback
 * @param param2 - Second parameter passed to callback
 * @param title - Dialog title
 * @param message - Dialog message
 * @param catchCallback - Function to execute on cancellation
 * @returns Promise that resolves when dialog closes
 */
function _openConfirm(
	options: ConfirmOptions,
	callback: ConfirmCallback,
	param1?: any,
	param2?: any,
	title?: string,
	message?: string,
	catchCallback: () => void = () => {},
): Promise<void> {
	// Accesso diretto a Element UI $confirm tramite Vue.prototype (più stabile)
	const $confirm = (Vue.prototype as any).$confirm;

	if (!$confirm) {
		console.error('❌ ConfirmService: $confirm not available on Vue prototype');
		return Promise.reject(new Error('$confirm not available'));
	}

	return $confirm(message, title, options)
		.then(() => {
			try {
				if (typeof callback === 'function') {
					callback(param1, param2);
				} else {
					console.error('❌ ConfirmService: callback is not a function!', callback);
				}
			} catch (error) {
				console.error('❌ ConfirmService: Error executing callback:', error);
			}
		})
		.catch(() => {
			try {
				catchCallback();
			} catch (error) {
				console.error('❌ ConfirmService: Error executing catchCallback:', error);
			}
		});
}

/**
 * Confirmation dialog service with multiple preset styles.
 * TypeScript version maintains full backward compatibility with JavaScript implementation.
 */
export default {
	/**
	 * Opens a green success confirmation dialog.
	 * @description Displays success-styled dialog and executes callback on confirmation.
	 * @param callback - Function to execute on confirmation
	 * @param param1 - First optional parameter for callback
	 * @param param2 - Second optional parameter for callback
	 * @param title - Dialog title (default: 'Continuare?')
	 * @param message - Dialog message (default: 'Sei sicuro di confermare?')
	 * @param catchCallback - Function to execute on cancellation
	 * @returns Promise that resolves when dialog closes
	 */
	openSuccessConfirm(
		callback: ConfirmCallback = () => {},
		param1: any = null,
		param2: any = null,
		title = 'Continuare?',
		message = 'Sei sicuro di confermare?',
		catchCallback = () => {},
	): Promise<void> {
		const options: ConfirmOptions = {
			confirmButtonText: 'OK',
			cancelButtonText: 'Annulla',
			type: 'success',
			confirmButtonClass: 'el-button--success',
		};
		return _openConfirm(options, callback, param1, param2, title, message, catchCallback);
	},

	/**
	 * Opens an orange warning confirmation dialog.
	 * @description Displays warning-styled dialog for irreversible operations.
	 * @param callback - Function to execute on confirmation
	 * @param param1 - First optional parameter for callback
	 * @param param2 - Second optional parameter for callback
	 * @param title - Dialog title (default: 'Attenzione!')
	 * @param message - Dialog message (default: 'Attenzione l\'operazione è irreversibile. Continuare?')
	 * @param catchCallback - Function to execute on cancellation
	 * @returns Promise that resolves when dialog closes
	 */
	openWarningConfirm(
		callback: ConfirmCallback = () => {},
		param1: any = null,
		param2: any = null,
		title = 'Attenzione!',
		message = "Attenzione l'operazione è irreversibile. Continuare?",
		catchCallback = () => {},
	): Promise<void> {
		const options: ConfirmOptions = {
			confirmButtonText: 'OK',
			cancelButtonText: 'Annulla',
			type: 'warning',
			confirmButtonClass: 'el-button--warning',
		};
		return _openConfirm(options, callback, param1, param2, title, message, catchCallback);
	},

	/**
	 * Opens a red error confirmation dialog.
	 * @description Displays error-styled dialog for error recovery actions.
	 * @param callback - Function to execute on confirmation
	 * @param param1 - First optional parameter for callback
	 * @param param2 - Second optional parameter for callback
	 * @param title - Dialog title (default: 'Errore!')
	 * @param message - Dialog message (default: 'Si è verificato un errore. Riprovare?')
	 * @param catchCallback - Function to execute on cancellation
	 * @returns Promise that resolves when dialog closes
	 */
	openErrorConfirm(
		callback: ConfirmCallback = () => {},
		param1: any = null,
		param2: any = null,
		title = 'Errore!',
		message = 'Si è verificato un errore. Riprovare?',
		catchCallback = () => {},
	): Promise<void> {
		const options: ConfirmOptions = {
			confirmButtonText: 'OK',
			cancelButtonText: 'Annulla',
			type: 'error',
			confirmButtonClass: 'el-button--danger',
		};
		return _openConfirm(options, callback, param1, param2, title, message, catchCallback);
	},

	/**
	 * Opens a blue info confirmation dialog.
	 * @description Displays info-styled dialog for informational confirmations.
	 * @param callback - Function to execute on confirmation
	 * @param param1 - First optional parameter for callback
	 * @param param2 - Second optional parameter for callback
	 * @param title - Dialog title (default: 'Info title')
	 * @param message - Dialog message (default: 'Messaggio info. Continuare?')
	 * @param catchCallback - Function to execute on cancellation
	 * @returns Promise that resolves when dialog closes
	 */
	openInfoConfirm(
		callback: ConfirmCallback = () => {},
		param1: any = null,
		param2: any = null,
		title = 'Info title',
		message = 'Messaggio info. Continuare?',
		catchCallback = () => {},
	): Promise<void> {
		const options: ConfirmOptions = {
			confirmButtonText: 'OK',
			cancelButtonText: 'Annulla',
			type: 'info',
			confirmButtonClass: 'el-button--info',
		};
		return _openConfirm(options, callback, param1, param2, title, message, catchCallback);
	},
};
